import { ComponentProps } from "react";
import { EditorContent as TiptapEditorContent, BubbleMenu as TiptapBubbleMenu } from "@tiptap/react";
import { styled } from "@mui/material/styles";

interface StyledEditorContentProps extends ComponentProps<typeof TiptapEditorContent> {
  $minHeight?: number | string;
}

export const EditorContent = styled(TiptapEditorContent, {
  shouldForwardProp: (prop) => prop !== '$minHeight',
})<StyledEditorContentProps>(({ theme, $minHeight }) => ({
  div: {
    width: '100%',
    minHeight: typeof $minHeight === 'number' ? `${$minHeight}px` : $minHeight,
    padding: "0px 16px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    outline: "none",
  },

  blockquote: {
    borderLeft: `4px solid ${theme.palette.divider}`,
    margin: theme.spacing(2, 0),
    padding: theme.spacing(1, 2),
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    backgroundColor: theme.palette.action.hover,
  },

  pre: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflowX: "auto",
    fontFamily: "monospace",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    border: `1px solid ${theme.palette.divider}`,
  },

  code: {
    fontFamily: "monospace",
    color: theme.palette.secondary.main,
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius / 2,
  },

  "& .tiptap p.is-empty::before": {
    color: theme.palette.text.disabled,
    content: "attr(data-placeholder)",
    float: "left",
    height: 0,
    pointerEvents: "none",
  },
}));

export const BubbleMenu = styled(TiptapBubbleMenu)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
}));

interface BubbleButtonProps extends ComponentProps<"button"> {
  "data-active"?: boolean;
}

export const BubbleButton = styled(({ type = "button", ...props }: BubbleButtonProps) => (
  <button type={type} {...props} />
))(({ theme }) => ({
  padding: theme.spacing(1),
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: "none",
  backgroundColor: "transparent",
  border: "none",
  cursor: "pointer",
  transition: "color 0.2s, background-color 0.2s",

  "&:hover": {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[200],
  },

  '&[data-active="true"]': {
    color: theme.palette.primary.main,
  },
}));

export interface FloatingMenuButtonProps extends ComponentProps<"button"> {
  imgURL: string;
  title: string;
  description: string;
}

export const FloatingMenuButton = styled(
  ({ imgURL, title, description, ...props }: FloatingMenuButtonProps) => (
    <button type="button" {...props}>
      <img src={imgURL} alt={title} />
      <div>
        <span>{title}</span>
        <span>{description}</span>
      </div>
    </button>
  )
)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  width: 300,
  border: "none",
  cursor: "pointer",
  transition: "background-color 0.2s",

  "&:hover": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[300],
  },

  "& img": {
    width: theme.spacing(6),
    height: theme.spacing(6),
    backgroundColor: "#FFFFFF",
    borderRadius: theme.shape.borderRadius,
  },

  "& div": {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
  },

  "& div span:first-of-type": {
    fontSize: "0.875rem",
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },

  "& div span:last-of-type": {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
  },
}));
